/**
 * Живая витрина дизайн-системы Рублокса — «Storybook в коде».
 *
 * Отличие от документа-артефакта: там компоненты показаны в вакууме,
 * здесь — внутри настоящего кабинета и в АКТИВНОЙ теме пользователя.
 * Это единственный способ увидеть, что кнопка не разваливается на
 * «Закате» с оранжевым акцентом и на «Лесе» с зелёным.
 *
 * Правило: новый компонент попадает сюда в том же коммите, где появляется.
 * Витрина, отставшая от кода, хуже отсутствующей — в школьной системе
 * ровно так и вышло: заявлено «показывает все компоненты», а табов,
 * поля пароля и рамок аватара в ней нет.
 *
 * Открыть: /app/ui
 */
import React, { useState } from "react";
import {
  Button, Pill, Card, Input, Textarea, EmptyState, Skeleton, Spinner,
  Tabs, Segmented, Stat, Section, fmtNum, fmtCompact,
} from "../../ui";
import CabIcon from "../CabIcon";
import { CAB_THEMES } from "../../../theme/cabThemes";
import { useCabinet } from "../../../context/CabinetContext";

const VARIANTS = [
  ["primary", "Играть", "Главное действие экрана. Ровно одна на экран."],
  ["solid", "Опубликовать", "Сильное, но не главное."],
  ["ghost", "Редактировать", "Обычное действие. Сколько угодно."],
  ["subtle", "В избранное", "Переключаемое состояние."],
  ["danger", "Удалить", "Необратимое. Всегда с подтверждением."],
  ["link", "Показать все", "Навигация под видом кнопки."],
];

const TONES = [
  ["accent", "Хит недели"], ["soft", "Гонки"], ["neutral", "Уровень 42"],
  ["success", "Опубликована"], ["warning", "На проверке"],
  ["danger", "Отклонена"], ["info", "Новинка"],
];

export default function UiScreen() {
  const [tab, setTab] = useState("buttons");
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(false);
  /* effectiveTheme — тема, которой кабинет РЕАЛЬНО покрашен сейчас
     (во время примерки в магазине она отличается от выбранной). Витрина
     должна называть то, что человек видит, иначе подпись врёт. */
  const { effectiveTheme } = useCabinet();
  const theme = CAB_THEMES[effectiveTheme] || CAB_THEMES.classic;

  const demoLoad = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1600);
  };

  return (
    <div className="ui-page">
      <Section
        title="Дизайн-система — живая витрина"
        sub={`Активная тема: «${theme.title}» (${theme.isDark ? "тёмная" : "светлая"}). Всё ниже красится ею автоматически — компоненты написаны на семантических токенах и не знают, какая тема включена.`}
      />

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { id: "buttons", label: "Кнопки" },
          { id: "forms", label: "Поля" },
          { id: "data", label: "Состояния" },
          { id: "nav", label: "Навигация" },
          { id: "misc", label: "Метки и статы" },
        ]}
      />

      <div style={{ marginTop: "var(--sp-6)" }}>
        {tab === "buttons" && (
          <>
            <Card style={{ marginBottom: "var(--sp-4)" }}>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Варианты — по смыслу, не по цвету
              </h3>
              <div className="ui-row" style={{ marginBottom: "var(--sp-5)" }}>
                {VARIANTS.map(([v, label]) => (
                  <Button key={v} variant={v}>{label}</Button>
                ))}
              </div>
              <div className="ui-table-card">
                <div className="ui-table-scroll">
                  <table className="ui-table">
                    <thead>
                      <tr><th>Вариант</th><th>Когда</th></tr>
                    </thead>
                    <tbody>
                      {VARIANTS.map(([v, , when]) => (
                        <tr key={v}><td><b>{v}</b></td><td>{when}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

            <Card style={{ marginBottom: "var(--sp-4)" }}>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Размеры — радиус меняется вместе с паддингом
              </h3>
              <div className="ui-row">
                <Button variant="primary" size="sm">Small</Button>
                <Button variant="primary">Medium</Button>
                <Button variant="primary" size="lg">Large</Button>
                <Button variant="ghost" iconOnly aria-label="Настройки"
                        icon={<CabIcon name="gear" size={17} />} />
                <Button variant="ghost" size="sm" iconOnly aria-label="Избранное"
                        icon={<CabIcon name="star" size={15} />} />
              </div>
            </Card>

            <Card>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Состояния
              </h3>
              <div className="ui-row" style={{ marginBottom: "var(--sp-4)" }}>
                <Button variant="primary">Покой</Button>
                <Button variant="primary" disabled>Выключена</Button>
                <Button variant="primary" loading={loading} onClick={demoLoad}>
                  Нажми — загрузится
                </Button>
                <Button variant="ghost">Tab сюда → кольцо</Button>
              </div>
              <p className="ui-field__hint">
                Наведи на «Выключена» — она не реагирует. Каждое hover-правило
                написано через <code>:hover:not(:disabled)</code>, иначе выключенная
                кнопка подсвечивается и выглядит нажимаемой.
              </p>
            </Card>
          </>
        )}

        {tab === "forms" && (
          <div className="ui-grid-2">
            <Card>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Рабочие
              </h3>
              <div style={{ display: "grid", gap: "var(--sp-4)" }}>
                <Input
                  label="Название игры"
                  placeholder="как игроки увидят её в каталоге"
                  hint="До 60 символов. Потом можно изменить."
                />
                <Input
                  label="Поиск"
                  placeholder="найти игру или автора…"
                  leadIcon={<CabIcon name="search" size={17} />}
                />
                <Textarea
                  label="Описание"
                  placeholder="о чём игра и что в ней делать"
                  hint="Появится на странице игры."
                />
              </div>
            </Card>
            <Card>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Проблемные
              </h3>
              <div style={{ display: "grid", gap: "var(--sp-4)" }}>
                <Input
                  label="Почта"
                  defaultValue="igrok@"
                  error="Не хватает домена — например, igrok@mail.ru"
                  hint="Эта подсказка не видна: ошибка её вытесняет"
                />
                <Input
                  label="Ник"
                  defaultValue="minecraftia"
                  disabled
                  hint="Ник меняется раз в 30 дней."
                />
              </div>
              <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)" }}>
                Ошибка вытесняет подсказку: две строки под полем — два
                конкурирующих сообщения, второе не читают.
              </p>
            </Card>
          </div>
        )}

        {tab === "data" && (
          <div className="ui-grid-2">
            <div style={{ display: "grid", gap: "var(--sp-4)", alignContent: "start" }}>
              <Card pad0>
                <EmptyState
                  icon={<CabIcon name="search" size={40} />}
                  title="Пока ничего не найдено"
                  desc="Попробуй другое слово или убери фильтры."
                  action={<Button variant="ghost" size="sm">Сбросить фильтры</Button>}
                />
              </Card>
              <Card pad0>
                <EmptyState
                  icon={<CabIcon name="gamepad" size={40} />}
                  title="Здесь появятся твои игры"
                  desc="Создай первую в Студии — это бесплатно и без установки."
                  action={<Button variant="primary" size="sm">Открыть Студию</Button>}
                />
              </Card>
            </div>
            <div style={{ display: "grid", gap: "var(--sp-4)", alignContent: "start" }}>
              <Card>
                <div style={{ display: "grid", gap: 10 }}>
                  <Skeleton w="62%" h={22} />
                  <Skeleton />
                  <Skeleton w="84%" />
                </div>
                <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)" }}>
                  Скелетон — когда форма контента известна. Держит раскладку:
                  после загрузки ничего не прыгает.
                </p>
              </Card>
              <Card style={{ display: "grid", placeItems: "center", padding: "var(--sp-10)" }}>
                <Spinner />
                <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)", textAlign: "center" }}>
                  Спиннер — когда грузится страница целиком и форма неизвестна.
                </p>
              </Card>
            </div>
          </div>
        )}

        {tab === "nav" && (
          <div className="ui-grid-2">
            <Card>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Подчёркивание — разделы
              </h3>
              <Tabs
                value={tab === "nav" ? "all" : "all"}
                onChange={() => {}}
                items={[
                  { id: "all", label: "Все игры" },
                  { id: "top", label: "Популярные" },
                  { id: "new", label: "Новинки" },
                  { id: "friends", label: "Друзья играют" },
                ]}
              />
              <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)" }}>
                Для <b>разного</b> содержимого. Вкладка = раздел = свой URL.
                Может быть много, лента прокручивается свайпом.
              </p>
            </Card>
            <Card>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Сегмент-контрол — режим показа
              </h3>
              <Segmented
                value={view}
                onChange={setView}
                items={[{ id: "grid", label: "Сетка" }, { id: "list", label: "Список" }]}
              />
              <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)" }}>
                Для <b>одного</b> содержимого в разных видах. Строго 2–3 варианта,
                всегда видны все. Больше трёх — выпадающий список.
              </p>
            </Card>
          </div>
        )}

        {tab === "misc" && (
          <>
            <Card style={{ marginBottom: "var(--sp-4)" }}>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Пилюли — 7 тонов
              </h3>
              <div className="ui-row">
                {TONES.map(([t, label]) => (
                  <Pill key={t} tone={t}>{label}</Pill>
                ))}
              </div>
              <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)" }}>
                Заливка <code>--X-bg</code>, текст <code>--X</code>. Только
                neutral имеет границу: без своей заливки он сливается с карточкой.
              </p>
            </Card>
            <Card>
              <h3 className="ui-section__title" style={{ marginBottom: "var(--sp-4)" }}>
                Статы — числа с tabular-nums
              </h3>
              <div className="ui-grid-2">
                <Stat value={fmtNum(12480)} label="запусков" />
                <Stat value={fmtNum(340)} label="игроков сейчас" />
                <Stat value={fmtCompact(12480)} label="запусков (сокращённо)" />
                <Stat value={fmtCompact(1240000)} label="показов" />
              </div>
              <p className="ui-field__hint" style={{ marginTop: "var(--sp-4)" }}>
                В пропорциональном шрифте «1» уже остальных цифр — обновляясь
                с 999 на 1000, число дёргает всю строку.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
